import { createScheduledActionsAPI } from '../api/impl/ScheduledActionsAPI';
import type { DelayedAction } from '../api/ReplicantAPI';
import { EXIT_FINAL } from '../common/Analytics';
import type { ErrorReporter } from '../common/ErrorReporter';
import type { default as DB, MetainfoMVCC } from '../db/DB';
import type { ReplicantConfig } from '../ReplicantConfig';
import type { ScheduledActions } from '../ReplicantScheduledActions';
import SB from '../SchemaBuilder';
import { deepCopy } from '../utils/ObjUtils';
import { duration } from '../utils/Utils';

/** The user ID used by global system scheduled actions, i.e., scheduled actions not targeting a single user. */
export const GLOBAL_SYSTEM_SCHEDULED_ACTION_USER_ID = '_globalSystemScheduledActionUser';

export const SYSTEM_SCHEDULED_ACTION_NAMES = {
    onGameEnd: 'onGameEnd',
    onSessionEnd: 'onSessionEnd',
    lineChatbotBroadcast: 'lineChatbotBroadcast',
} as const;

const systemScheduledActionsSchema = {
    [SYSTEM_SCHEDULED_ACTION_NAMES.onGameEnd]: SB.object({
        eventData: SB.object({
            contextId: SB.string().optional(),
            contextType: (SB.string() as any as SB.TupleSchema<'SOLO' | 'THREAD' | 'GROUP'>).optional(), // skip tuple validation to avoid breaking onGameEnd whenever FB adds a new context type
            messengerId: SB.string(),
            payload: SB.map(SB.unknown()).optional(),
            playerId: SB.string(),
            score: SB.int().optional(),
        }),
        isFirstInSession: SB.boolean(),
    }),
    [SYSTEM_SCHEDULED_ACTION_NAMES.onSessionEnd]: SB.object({
        // TODO: make arguments required in a future release. The arguments are optional to handle backwards compatibility.
        sessionDurationMs: SB.int().optional(),
        sessionId: SB.string().optional(),
    }),
    [SYSTEM_SCHEDULED_ACTION_NAMES.lineChatbotBroadcast]: SB.object({
        broadcastId: SB.string(),
        broadcastOpts: SB.map(SB.unknown()),
    }),
} satisfies Record<keyof typeof SYSTEM_SCHEDULED_ACTION_NAMES, SB.Schema>;

/**
 * A set of hard-coded system scheduled actions definitions.
 *
 * These actions are only usable internally in the server code and are not exposed through `api.scheduledActions`.
 */
export type SystemScheduledActions = ScheduledActions<any, typeof systemScheduledActionsSchema>;

export function createSystemScheduledActions(opts: {
    config: ReplicantConfig<any>;
    db: DB<any>;
}): SystemScheduledActions {
    return {
        onGameEnd: {
            schema: systemScheduledActionsSchema.onGameEnd,
            action: async (state, args, api) => {
                if (!opts.config.chatbot?.events.onGameEnd) {
                    throw Error('No `onGameEnd` handler defined in chatbot configuration');
                }

                await opts.config.chatbot.events.onGameEnd(state, args.eventData, api, args.isFirstInSession);

                if (args.eventData.payload?.sessionId && typeof args.eventData.payload.sessionId === 'string') {
                    await opts.db.chatbot.setSessionId(args.eventData.playerId, args.eventData.payload.sessionId);
                }
            },
        },
        onSessionEnd: {
            schema: systemScheduledActionsSchema.onSessionEnd,
            action: async (state, args, api) => {
                const onSessionEnd = opts.config.eventHandlers?.onSessionEnd;

                let customExitFinalEventProps: Record<string, unknown> = {};

                if (onSessionEnd) {
                    const handlerResult = await onSessionEnd(state, {}, api);

                    if (handlerResult && handlerResult.exitFinalEventProperties) {
                        customExitFinalEventProps = handlerResult.exitFinalEventProperties;
                    }
                }

                const { sessionDurationMs, sessionId } = args;

                const exitFinalEvent = {
                    eventType: EXIT_FINAL,
                    eventProperties: {
                        ...customExitFinalEventProps,
                        sessionElapsedTime: sessionDurationMs
                            ? sessionDurationMs / duration({ seconds: 1 })
                            : undefined,
                        sessionElapsedMinutes: sessionDurationMs
                            ? sessionDurationMs / duration({ minutes: 1 })
                            : undefined,
                    },
                    insertId: sessionId ? `${EXIT_FINAL}-${sessionId}` : undefined, // Avoid duplicate events in case of scheduled action retry
                };

                api.sendAnalyticsEvents([exitFinalEvent]);
            },
        },
        lineChatbotBroadcast: {
            schema: systemScheduledActionsSchema.lineChatbotBroadcast,
            action: () => undefined, // Unused: this is a global scheduled action and doesn't target an individual user. See event handler implementation in `ServerReplicantScheduledActions.executeGlobalSystemScheduledAction`.
        },
    };
}

export class ServerReplicantSystemScheduledActions {
    schedule = {} as {
        [K in keyof SystemScheduledActions]: (opts: {
            args: SB.ExtractType<SystemScheduledActions[K]['schema']>;
            delayInMS: number;
            notificationId: string;
            userData: { metainfoMVCC: MetainfoMVCC };
            userId: string;
        }) => Promise<void>;
    };

    scheduleGlobal = {} as {
        [K in 'lineChatbotBroadcast']: (opts: {
            args: SB.ExtractType<SystemScheduledActions[K]['schema']>;
            delayInMS: number;
            notificationId: string;
        }) => Promise<void>;
    };

    constructor(
        private opts: {
            config: ReplicantConfig<any>;
            dateNow: () => number;
            db: DB<any>;
            errorReporter: ErrorReporter;
            resolveDelayedActions: (delayedActions: DelayedAction[]) => Promise<void>;
        },
    ) {
        const globalActionNames = [SYSTEM_SCHEDULED_ACTION_NAMES.lineChatbotBroadcast];
        const systemScheduledActions = createSystemScheduledActions({ config: opts.config, db: opts.db });

        for (const actionName in systemScheduledActions) {
            const key = actionName as keyof SystemScheduledActions;

            this.schedule[key] = async ({ args, delayInMS, notificationId, userData, userId }) => {
                systemScheduledActions[key].schema.tryValidate(args);

                const delayedActions: DelayedAction[] = [];

                if (globalActionNames.includes(key as any) && userId !== GLOBAL_SYSTEM_SCHEDULED_ACTION_USER_ID) {
                    throw Error(`Cannot schedule global system scheduled action ${key} for user ${userId}`);
                }

                const api = createScheduledActionsAPI({
                    db: this.opts.db,
                    delayedActions,
                    errorReporter: this.opts.errorReporter,
                    invokeTime: this.opts.dateNow(),
                    isSystemAction: true,
                    scheduledActions: systemScheduledActions,
                    userData: { metainfoMVCC: deepCopy(userData.metainfoMVCC) }, // don't mutate `userData` argument
                    userId,
                });

                api.schedule[key]({ args: args as any, delayInMS, notificationId });

                await this.opts.resolveDelayedActions(delayedActions);
            };
        }

        for (const globalActionName of globalActionNames) {
            this.scheduleGlobal[globalActionName] = async ({ args, delayInMS, notificationId }) => {
                const metainfoMVCC = await this.opts.db.metainfoMVCC.fetchMetainfoMVCC(
                    GLOBAL_SYSTEM_SCHEDULED_ACTION_USER_ID,
                );

                await this.schedule[globalActionName]({
                    args,
                    delayInMS,
                    notificationId,
                    userData: { metainfoMVCC },
                    userId: GLOBAL_SYSTEM_SCHEDULED_ACTION_USER_ID,
                });
            };
        }
    }

    async unschedule(opts: {
        notificationId: string;
        userData: { metainfoMVCC: MetainfoMVCC };
        userId: string;
    }): Promise<void> {
        const delayedActions: DelayedAction[] = [];

        const api = createScheduledActionsAPI({
            db: this.opts.db,
            delayedActions,
            errorReporter: this.opts.errorReporter,
            invokeTime: this.opts.dateNow(),
            isSystemAction: true,
            scheduledActions: {},
            userData: { metainfoMVCC: deepCopy(opts.userData.metainfoMVCC) }, // don't mutate `userData` argument
            userId: opts.userId,
        });

        api.unschedule(opts.notificationId);

        await this.opts.resolveDelayedActions(delayedActions);
    }

    async unscheduleGlobal(notificationId: string): Promise<void> {
        const metainfoMVCC = await this.opts.db.metainfoMVCC.fetchMetainfoMVCC(GLOBAL_SYSTEM_SCHEDULED_ACTION_USER_ID);

        await this.unschedule({
            notificationId,
            userData: { metainfoMVCC },
            userId: GLOBAL_SYSTEM_SCHEDULED_ACTION_USER_ID,
        });
    }
}
